import { useCallback, useEffect, useRef } from "react";

/**
 * Host open requests (`defaultOpen` replay, `Wexio('open')`, a controlled `open` flip, `previewTab`) can
 * arrive while the widget cannot open yet (config or the visitor handshake still loading). The launcher just
 * ignores a tap then, but a host command is one-shot: dropping it loses the intent. This keeps the latest
 * intent and applies it once `blocked` clears; `cancel()` (a later close) discards it, so the latest wins.
 * `apply` runs the shell's guarded open (`openPanel`).
 *
 * An auth ERROR is a block that will not clear by itself, so a held request is applied the moment `errored`
 * turns true: the guarded open then shows the "couldn't connect / try again" popup instead of leaving the
 * host's open silently pending for ever.
 *
 * `store` holds the pending flag. The shell remounts once when the config answers (the demo placeholder gives
 * way to the live tree), and a one-shot host command that arrived before that would be lost with the hook's own
 * ref, so the shell passes a store that outlives the mount (`WidgetInstance.pendingOpen`).
 */
export function useDeferredOpen(
  blocked: boolean,
  apply: () => void,
  errored = false,
  store?: { current: boolean },
) {
  const own = useRef(false);
  const pending = store ?? own;
  const erroredRef = useRef(errored);
  erroredRef.current = errored;
  const blockedRef = useRef(blocked);
  blockedRef.current = blocked;
  const applyRef = useRef(apply);
  applyRef.current = apply;

  const request = useCallback(() => {
    if (blockedRef.current && !erroredRef.current) pending.current = true;
    else applyRef.current();
  }, [pending]);
  const cancel = useCallback(() => {
    pending.current = false;
  }, [pending]);

  useEffect(() => {
    if (blocked || !pending.current) return;
    pending.current = false;
    applyRef.current();
  }, [blocked, pending]);

  useEffect(() => {
    if (!errored || !pending.current) return;
    pending.current = false;
    applyRef.current();
  }, [errored, pending]);

  return { request, cancel };
}
